import { css, cx } from "@emotion/css";
import { Button, useCurrentScrollArea } from "@octopusdeploy/design-system-components";
import { fontSize, space, themeTokens, borderRadius, text } from "@octopusdeploy/design-system-tokens";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import * as React from "react";
import Chip from "~/components/Chips/Chip";
import Logo from "~/components/Logo/index";
import Markdown from "~/components/Markdown";
type StepCardProps = {
    heading: string;
    subHeading?: string;
    logo: string | React.ReactNode;
    chipType?: "primary" | "info" | "success";
    chipLabel?: string;
    description?: string;
    dotpoints?: string[];
    truncateDotpoints?: boolean;
    primaryAction?: {
        label: string;
        href: LinkHref;
        external?: boolean;
        onClick?: () => void;
    } | {
        label: string;
        onClick: () => void;
    };
    secondaryAction?: {
        label: string;
        href: LinkHref;
        external?: boolean;
        onClick?: () => void;
    } | {
        label: string;
        onClick: () => void;
    };
    disabled?: boolean;
};
export function StepCard({ chipType = "primary", chipLabel, disabled, ...props }: StepCardProps) {
    const currentScrollArea = useCurrentScrollArea();
    const triggerPrimaryActionAndResetScrollPosition = () => {
        if (currentScrollArea?.scroll instanceof Function) {
            currentScrollArea.scroll({ top: 0, behavior: "instant" });
        }
        if (props.primaryAction && props.primaryAction.onClick) {
            props.primaryAction.onClick();
        }
    };
    return (<div className={disabled ? cx(styles.base, styles.disabled) : cx(styles.base, styles.shadow)}>
            <div>
                <div className={styles.header}>
                    {typeof props.logo === "string" ? <Logo url={props.logo} size={space[40]} isSquare={true}/> : props.logo}
                    {chipLabel && (<Chip backgroundColor={themeTokens.color.chip.filled.background[chipType]} labelColor={themeTokens.color.chip.filled.text[chipType]} noMargin={true} noTooltip={true}>
                            {chipLabel}
                        </Chip>)}
                </div>
                <div className={styles.heading}>{props.heading}</div>
                {props.subHeading && <div className={styles.subHeading}>{props.subHeading}</div>}
                {props.description && (<div className={styles.description}>
                        <Markdown markup={props.description} noMargins mutedTextColor/>
                    </div>)}
                {props.dotpoints && props.dotpoints.length > 2 && props.truncateDotpoints ? (<>
                        {props.dotpoints.slice(0, 2).map((dotpoint) => (<div key={dotpoint} className={styles.featureContainer}>
                                <div className={styles.featureIcon}>
                                    <i className="fa-regular fa-circle-check"/>
                                </div>
                                <div className={styles.featureText}>{dotpoint}</div>
                            </div>))}
                        <div className={styles.featureText}>+ {props.dotpoints.length - 2} more</div>
                    </>) : (props.dotpoints?.map((dotpoint) => (<div key={dotpoint} className={styles.featureContainer}>
                            <div className={styles.featureIcon}>
                                <i className="fa-regular fa-circle-check"/>
                            </div>
                            <div className={styles.featureText}>{dotpoint}</div>
                        </div>)))}
            </div>

            <div className={styles.buttonsContainer}>
                {props.primaryAction ? ("href" in props.primaryAction ? (<Button importance={"secondary"} size={"small"} label={props.primaryAction.label} disabled={disabled} href={props.primaryAction.href} external={props.primaryAction.external} onClick={triggerPrimaryActionAndResetScrollPosition}/>) : (<Button importance={"secondary"} size={"small"} label={props.primaryAction.label} disabled={disabled} onClick={triggerPrimaryActionAndResetScrollPosition}/>)) : undefined}
                {props.secondaryAction ? ("href" in props.secondaryAction ? (<Button importance={"quiet"} size={"small"} opensInNewTab={true} label={props.secondaryAction.label} href={props.secondaryAction.href} onClick={props.secondaryAction.onClick} external={props.secondaryAction.external}/>) : (<Button importance={"quiet"} size={"small"} label={props.secondaryAction.label} onClick={props.secondaryAction.onClick}/>)) : undefined}
            </div>
        </div>);
}
StepCard.displayName = "StepCard";
const secondaryActionButtonLabel = "step-tile-secondary-action-button";
const styles = {
    base: css({
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        width: "100%",
        padding: space[16],
        backgroundColor: themeTokens.color.card.background.primary.default,
        borderRadius: borderRadius.small,
        WebkitFontSmoothing: "antialiased",
    }),
    shadow: css({
        boxShadow: themeTokens.shadow.small,
    }),
    disabled: css({
        opacity: 0.5,
        border: `1px solid ${themeTokens.color.border.primary}`,
    }),
    header: css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
    }),
    heading: css({
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        font: text.body.bold.large,
        color: themeTokens.color.text.primary,
        marginTop: space[8],
    }),
    subHeading: css({
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.secondary,
    }),
    description: css({
        display: "-webkit-box",
        WebkitLineClamp: 4,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        fontSize: fontSize.small,
        color: themeTokens.color.text.secondary,
        marginTop: space[8],
        marginBottom: space[8],
    }),
    featureContainer: css({
        display: "flex",
        alignItems: "baseline",
        marginTop: space[8],
        marginBottom: space[8],
    }),
    featureIcon: css({
        marginRight: space[4],
        position: "relative",
        top: "1px",
        color: themeTokens.color.icon.secondary,
    }),
    featureText: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
    }),
    buttonsContainer: css({
        display: "flex",
        alignItems: "center",
        marginTop: space[12],
        justifyContent: "flex-start",
        flexWrap: "wrap",
        gap: space[8],
    }),
    secondaryActionButton: css({
        label: secondaryActionButtonLabel,
        i: {
            color: themeTokens.color.button.icon.quiet,
            fontSize: fontSize.large,
        },
    }),
};
